import { digObject } from '@domain-group/javascript-utils'

import type { EntitiesState } from '@redux/entities'

import type { RelatedEntityModel } from '@domain-group/realhub-backend-api-types'

export const templatePageTypes: Record<string, string> = {
  new: 'New',
  origin: 'Origin',
}

export const userProjectRoles: Record<string, string> = {
  project_owner: 'Project Owner',
  project_contact: 'Project Contact',
}

export type Setting = {
  callbacks?: {
    getAdvancedLabel?: (relatedEntity: RelatedEntityModel) => string,
    getLabel?: (relatedEntity: RelatedEntityModel) => string,
  },
  entityKey: string,
  label: string,
}

export type SubjectOrOwner = {
  settings: Setting,
  title: string,
}

export const entitySettings: { [key: string]: Setting } = {
  ArtworkPage: {
    callbacks: {
      getLabel: (relatedEntity: RelatedEntityModel) => {
        // @ts-expect-error TS2339
        const { artwork_page_id } = relatedEntity
        return `Artwork Page - #${artwork_page_id}`
      },
    },
    entityKey: 'artworkPages',
    label: 'Artwork Page',
  },
  Campaign: {
    entityKey: 'campaigns',
    label: 'Campaign',
  },
  Provider: {
    entityKey: 'providers',
    label: 'Provider',
  },
  ServiceProduct: {
    entityKey: 'serviceProducts',
    label: 'Service Product',
  },
  TaskListItem: {
    entityKey: 'taskListItems',
    label: 'Task List Item',
  },
  TemplatePage: {
    entityKey: 'templatePages',
    label: 'Template Page',
  },
  User: {
    callbacks: {
      getAdvancedLabel: (relatedEntity: RelatedEntityModel) => {
        const userProjectRole: string = digObject(relatedEntity, 'data.role', 'project_contact')
        const userProjectRoleLabel = userProjectRoles[userProjectRole] || 'Project Contact'

        return userProjectRoleLabel
      },
    },
    entityKey: 'users',
    label: 'User',
  },
}

export const matchesFilters = (
  params: {
    filters?: { [key: string]: any },
    relatedEntity: RelatedEntityModel,
  },
): boolean => {
  const { filters, relatedEntity } = params

  if (!filters) return true

  const matches = Object.keys(filters).every((filterKey) => {
    const filterValue = filters[filterKey]
    return digObject(relatedEntity, `${filterKey}`) === filterValue
  })

  return matches
}

type FindRelatedEntityParams = {
  entities: EntitiesState,
  filters?: { [key: string]: any },
  item: {
    id: number,
    type: string,
  },
  ownerType?: string,
  returnType: 'owner' | 'subject',
  subjectType?: string,
}

export const findRelatedEntity = (params: FindRelatedEntityParams): RelatedEntityModel | undefined => {
  const {
    entities,
    filters,
    item,
    ownerType,
    returnType = 'subject',
    subjectType,
  } = params

  const { relatedEntities } = entities

  // Owner
  if (returnType === 'owner'){
    const entity = Object.values(relatedEntities).find((relatedEntity) => {
      const { subject_id, owner_type, subject_type } = relatedEntity

      return matchesFilters({ filters, relatedEntity })
        && subject_id === item.id
        && subject_type === item.type
        && owner_type === ownerType
    })

    return entity
  }

  // Subject
  const entity = Object.values(relatedEntities).find((relatedEntity) => {
    const { owner_id, owner_type, subject_type } = relatedEntity

    return matchesFilters({ filters, relatedEntity })
      && owner_id === item.id
      && owner_type === item.type
      && subject_type === subjectType
  })

  return entity
}

type GetRelatedEntityParams = {
  entities: EntitiesState,
  filters?: { [key: string]: any },
  item: {
    id: number,
    type: string,
  },
  ownerType?: string,
  relatedEntity?: RelatedEntityModel,
  returnType: 'owner' | 'subject',
  subjectType?: string,
}

export const getRelatedEntityOwnerOrSubject = (params: GetRelatedEntityParams) => {
  const {
    entities,
    filters,
    item,
    ownerType,
    relatedEntity: suppliedRelatedEntity,
    returnType = 'subject',
    subjectType,
  } = params

  const relatedEntity = suppliedRelatedEntity || findRelatedEntity({
    entities,
    filters,
    item,
    ownerType,
    returnType,
    subjectType,
  })

  if (!relatedEntity) return {}

  const entityTypeKey = returnType === 'subject' ? relatedEntity.subject_type : relatedEntity.owner_type
  // @ts-expect-error TS2538
  const settings = entitySettings[entityTypeKey]
  const entityKey = settings?.entityKey
  if (!entityKey) return {}

  const owner = digObject(entities, `${entityKey}.${relatedEntity.owner_id}`, {})
  const subject = digObject(entities, `${entityKey}.${relatedEntity.subject_id}`, {})

  const entity = returnType === 'subject' ? subject : owner

  return {
    ...entity,
    settings,
  }
}

export default getRelatedEntityOwnerOrSubject
